import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, PenLine, Cpu, Plus, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const SRC = {
    auto: { l: "Auto", Icon: Zap, c: "#38BDF8" },
    manual: { l: "Manual", Icon: PenLine, c: "#C084FC" },
    system: { l: "System", Icon: Cpu, c: "#F43F5E" },
};
const dayKey = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA"); } catch { return ""; } };
const fmtDay = (d) => { try { return new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const INPUT = "rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#9D4CDD]/60 focus:outline-none";

export function ChangelogRow({ it, i }) {
    const s = SRC[it.source] || SRC.auto;
    return (
        <li className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`changelog-row-${i}`}>
            <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${s.c}22`, color: s.c }} title={s.l}><s.Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${s.c}22`, color: s.c }} data-testid={`changelog-page-${i}`}>{it.page}</span>
                    <span className="text-[11px] font-black text-white/70" data-testid={`changelog-account-${i}`}>{it.account}</span>
                </div>
                <p className="text-sm font-bold text-white mt-0.5 break-words" data-testid={`changelog-action-${i}`}>{it.action}</p>
            </div>
            <span className="text-[10px] text-white/40 tabular-nums whitespace-nowrap shrink-0 mt-1">{fmtTime(it.ts)}</span>
        </li>
    );
}

function AddEntry({ pages, onDone }) {
    const qc = useQueryClient();
    const [page, setPage] = useState("General");
    const [action, setAction] = useState("");
    const add = useMutation({
        mutationFn: () => api.post("/admin/changelog", { page, action }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["changelog"] }); toast.success("Entry added"); setAction(""); onDone(); },
        onError: () => toast.error("Could not add entry"),
    });
    return (
        <form onSubmit={(e) => { e.preventDefault(); if (action.trim()) add.mutate(); }} className="flex flex-col sm:flex-row gap-2 rounded-xl border border-[#C084FC]/40 bg-[#C084FC]/[0.06] p-3 mb-5" data-testid="changelog-add-form">
            <input list="changelog-pages" value={page} onChange={(e) => setPage(e.target.value)} placeholder="Page" className={`${INPUT} sm:w-44`} data-testid="changelog-add-page" />
            <datalist id="changelog-pages">{pages.map((p) => <option key={p} value={p} />)}</datalist>
            <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Exact action, e.g. Moved Sarah Fillier to 1st line" className={`${INPUT} flex-1`} data-testid="changelog-add-action" autoFocus />
            <button type="submit" disabled={!action.trim() || add.isPending} className="px-4 py-2 rounded-lg bg-[#C084FC] text-black text-xs font-black uppercase tracking-wider disabled:opacity-40" data-testid="changelog-add-submit">Add</button>
        </form>
    );
}

// Shared changelog view: the /changelog page AND the access-button Changelog popup.
export function ChangelogBody({ page, setPage, route = "" }) {
    const { user } = useAuth();
    const [src, setSrc] = useState("all");
    const [q, setQ] = useState("");
    const [adding, setAdding] = useState(false);
    const [here, setHere] = useState(!!route);
    const allowed = !!user && ["owner", "admin"].includes(user.role);
    const { data, isLoading } = useQuery({
        queryKey: ["changelog", src, page, q],
        queryFn: () => api.get("/admin/changelog", { params: { ...(src !== "all" ? { source: src } : {}), ...(page ? { page } : {}), ...(q.trim() ? { q: q.trim() } : {}), limit: 400 } }).then((r) => r.data),
        enabled: allowed, refetchInterval: 30 * 1000,
    });
    if (!allowed) return null;
    const onRoute = (it) => !!it.route && (route === it.route || route.startsWith(`${it.route}/`));
    const items = (data?.items || []).filter((it) => !(route && here) || onRoute(it));
    const byDay = items.reduce((m, it) => { const k = dayKey(it.ts); (m[k] = m[k] || []).push(it); return m; }, {});
    let n = 0;
    return (
        <>
            <div className="flex gap-1.5 flex-wrap mb-3">
                {route && (
                    <button type="button" onClick={() => setHere((v) => !v)} data-testid="changelog-this-page"
                        className={`px-3.5 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider ${here ? "bg-[#22D3EE] border-[#22D3EE] text-black" : "border-white/10 text-white/60 hover:text-white"}`}>This page</button>
                )}
                {[["all", "All"], ["auto", "Auto"], ["manual", "Manual"], ["system", "System"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setSrc(k)} data-testid={`changelog-tab-${k}`}
                        className={`px-3.5 py-1.5 rounded-lg border text-xs font-black uppercase tracking-wider ${src === k ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "border-white/10 text-white/60 hover:text-white"}`}>{l}</button>
                ))}
                <button type="button" onClick={() => setAdding((v) => !v)} data-testid="changelog-add-btn"
                    className="px-3.5 py-1.5 rounded-lg border border-[#C084FC]/50 text-[#C084FC] text-xs font-black uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#C084FC]/10"><Plus className="h-3.5 w-3.5" />Add entry</button>
            </div>
            <div className="flex gap-2 mb-5 flex-wrap">
                <select value={page} onChange={(e) => setPage(e.target.value)} className={`${INPUT} sm:w-52`} data-testid="changelog-page-filter">
                    <option value="">All pages</option>
                    {(data?.pages || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 pointer-events-none" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actions or accounts…" className={`${INPUT} w-full pl-9`} data-testid="changelog-search" />
                    {q && <button type="button" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X className="h-4 w-4" /></button>}
                </div>
            </div>
            {adding && <AddEntry pages={data?.pages || []} onDone={() => setAdding(false)} />}
            {isLoading ? <p className="text-white/50 text-sm">Loading…</p> : !items.length ? (
                <p className="text-white/50 text-sm py-10 text-center" data-testid="changelog-empty">No changes match.</p>
            ) : (
                <div className="space-y-6">
                    {Object.entries(byDay).sort(([a], [b]) => b.localeCompare(a)).map(([day, its]) => (
                        <section key={day || "none"}>
                            <h2 className="text-[11px] font-black uppercase tracking-wider text-white/45 mb-2">{day ? fmtDay(day) : "Undated"} · {its.length}</h2>
                            <ul className="space-y-2">{its.map((it) => <ChangelogRow key={n} it={it} i={n++} />)}</ul>
                        </section>
                    ))}
                </div>
            )}
        </>
    );
}

export default function Changelog() {
    const { user, loading } = useAuth();
    const [params, setParams] = useSearchParams();
    const page = params.get("page") || "";
    if (loading) return null;
    if (!(!!user && ["owner", "admin"].includes(user.role))) return <Navigate to="/" replace />;
    return (
        <div className="mx-auto max-w-4xl px-4 py-6 fade-up" data-testid="changelog-page">
            <h1 className="text-2xl font-black uppercase tracking-tight text-white mb-4">Changelog</h1>
            <ChangelogBody page={page} setPage={(v) => setParams(v ? { page: v } : {})} />
        </div>
    );
}
